/**
 * L'écran des Paramètres — les listes que chaque société règle elle-même.
 *
 * Maquette `docs/maquettes/parametres.html`, validée le 04/09/2026.
 *
 * TROIS LISTES, UN SEUL ÉCRAN, parce que ce sont trois fois les mêmes gestes :
 * ajouter, renommer, désactiver, supprimer si personne ne s'en sert. Ce qui les
 * distingue tient dans un descripteur — le titre, la présence d'un abrégé, le
 * mot qui compte l'emploi — et non dans trois composants qui divergeraient au
 * premier changement porté d'un seul côté.
 *
 * CE QUE CET ÉCRAN NE FAIT PAS
 * ---------------------------------------------------------------------------
 *   - il ne règle AUCUN droit : les rôles n'existent pas encore en v2, et
 *     quiconque entre dans Stafa peut déjà créer un client ;
 *   - il ne range pas les lignes à la main : trois à six lignes rangées par nom
 *     n'ont pas besoin d'un ordre réglable, contrairement aux camions du
 *     planning, que ni la plaque ni le nom ne rangent comme l'exploitation les
 *     veut ;
 *   - il ne touche pas à ce qui est DÉJÀ ENREGISTRÉ. Renommer un support ne
 *     réécrit pas les étapes : elles gardent le mot du jour du transport, comme
 *     elles gardent l'adresse du site. Le type d'un camion, lui, est une clé —
 *     le renommer corrige tous les camions d'un coup, et c'est voulu.
 */

import { useCallback, useEffect, useState } from 'react';
import { EcranImport } from './EcranImport.js';
import {
  creerLigneReglee,
  lireListeReglee,
  lireMotifsLitige,
  modifierLigneReglee,
  changerMotifLitige,
  creerMotifLitige,
  supprimerLigneReglee,
  supprimerMotifLitige,
  type EchecApi,
  type LigneReglee,
  type Resultat,
} from './api.js';
import {
  LARGEUR_DE_REFERENCE_CM,
  combienTiennent,
  metresLineaires,
  mlLisible,
  poser,
  refuse,
  type Support,
} from '@stafa/core';

/** Les trois listes de l'écran, dans l'ordre des onglets. */
export type CleListe = 'supports' | 'motifs' | 'types' | 'types_document';

interface Descripteur {
  readonly cle: CleListe;
  readonly onglet: string;
  readonly titre: string;
  /** Le mot du champ d'ajout : « Nom du support ». */
  readonly invite: string;
  readonly abrege: boolean;
  /**
   * Cette liste porte-t-elle des DIMENSIONS ? Les supports seuls.
   *
   * Elles ne sont PAS dans la barre d'ajout : six champs y feraient une ligne
   * illisible, et on ne connaît pas toujours les cotes au moment où l'on crée
   * un support. Elles vivent dans la boîte de modification, comme la maquette
   * `docs/maquettes/dimensions.html` le montre.
   */
  readonly dimensions: boolean;
  /**
   * Cette liste porte-t-elle le drapeau ÉCHANGEABLE ? Les supports seuls.
   *
   * Il commande tout le module Emballage : la boîte « Échange » d'une étape ne
   * propose que les supports cochés ici (`EcranOrdres.tsx` ligne 1138), et
   * l'écran Emballage ne compte que ceux-là (`apps/api/src/emballage.ts`
   * ligne 319, `where echangeable and actif`). Faux par défaut : le logiciel ne
   * devine pas lesquels des supports d'une société sont consignés.
   */
  readonly echange: boolean;
  /** Ce que compte `emploi` : « 142 étapes », « 3 camions ». */
  readonly compte: (combien: number) => string;
  /** L'aide affichée sous la barre — une phrase, jamais deux. */
  readonly aide: string;
}

export const LISTES: readonly Descripteur[] = [
  {
    cle: 'supports',
    onglet: 'Supports',
    titre: 'support',
    invite: 'Nom du support',
    abrege: true,
    dimensions: true,
    echange: true,
    compte: (n) => `${n} étape${n > 1 ? 's' : ''}`,
    aide: 'Le nom sert à la saisie d’une étape ; l’abrégé sert au planning, où la case est étroite. Renommer ne change rien aux étapes déjà enregistrées.',
  },
  {
    cle: 'motifs',
    onglet: 'Motifs de litige',
    titre: 'motif',
    invite: 'Nom du motif',
    abrege: false,
    dimensions: false,
    echange: false,
    // DES LITIGES, plus des étapes : depuis le 07/09/2026 le litige a sa table,
    // et un motif employé deux fois de suite sur la même étape compte deux
    // fois. C'est plus juste — ce sont bien deux incidents.
    compte: (n) => `${n} litige${n > 1 ? 's' : ''}`,
    aide: 'Ils se proposent à l’ouverture d’un litige sur une étape.',
  },
  {
    cle: 'types_document',
    onglet: 'Types de document',
    titre: 'type',
    invite: 'Nom du type',
    abrege: false,
    dimensions: false,
    echange: false,
    compte: (n) => `${n} document${n > 1 ? 's' : ''}`,
    aide: 'Ils classent les pièces jointes à un ordre de transport : commande client, lettre de voiture, bon de livraison signé…',
  },
  {
    cle: 'types',
    onglet: 'Types de véhicule',
    titre: 'type',
    invite: 'Nom du type',
    abrege: false,
    dimensions: false,
    echange: false,
    compte: (n) => `${n} camion${n > 1 ? 's' : ''}`,
    aide: 'Le type d’un camion pointe vers cette liste : le renommer corrige tous les camions qui le portent.',
  },
];

function descripteur(cle: CleListe): Descripteur {
  return LISTES.find((l) => l.cle === cle) ?? (LISTES[0] as Descripteur);
}

/**
 * Le chemin d'API d'un onglet. Les motifs de litige n'en ont pas ici : ils
 * existaient avant cet écran, sous `/motifs-litige`, et gardent leurs routes.
 */
function listeDe(cle: CleListe): 'supports' | 'types' | 'types_document' {
  if (cle === 'supports') return 'supports';
  if (cle === 'types_document') return 'types_document';
  return 'types';
}

type Etat =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'prete'; readonly lignes: readonly LigneReglee[] }
  | { readonly nom: 'panne' };

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette ligne n’existe plus. Rechargez l’écran.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce nom est déjà pris dans cette liste.',
  invalide: 'Le nom est obligatoire, et l’abrégé ne dépasse pas douze caractères.',
  conflit: 'Deux périodes se recouvrent.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache:
    'Des fiches s’y rattachent encore : désactivez cette ligne plutôt que de la supprimer.',
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee: 'Une étape déjà validée ne se déplace plus.',
  jour_sans_etape: 'Aucune étape ce jour-là.',
  // Ne vient jamais d'ici. La carte reste exhaustive, comme les autres.
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
};

/** Le renommage en cours, s'il y en a un. */
interface Renommage {
  readonly id: string;
  readonly nom: string;
  readonly abrege: string;
  /** En CENTIMÈTRES, et en CHAÎNE : un champ vide vaut « pas mesuré ». */
  readonly longueur: string;
  readonly largeur: string;
  readonly hauteur: string;
  readonly etages: string;
  readonly emploi: number;
}

/** Les champs de la boîte, pour ne pas les énumérer à chaque appel. */
type ChampBoite = 'nom' | 'abrege' | 'longueur' | 'largeur' | 'hauteur' | 'etages';

/**
 * Un champ de la boîte, en nombre pour l'API. Le vide devient `null` — c'est
 * ainsi qu'on RETIRE une dimension saisie à tort.
 *
 * Rend `undefined` si ce n'est pas un nombre : l'API refuserait, mais autant ne
 * pas lui envoyer. L'écran laisse alors la valeur inchangée plutôt que de
 * l'effacer en silence.
 */
function retenirSiConnu(
  cle: string,
  valeur: number | null | undefined,
): Record<string, number | null> {
  return valeur === undefined ? {} : { [cle]: valeur };
}

function nombreSaisi(valeur: string): number | null | undefined {
  const propre = valeur.trim();
  if (propre === '') return null;
  if (!/^[0-9]+$/.test(propre)) return undefined;
  return Number(propre);
}

// ---------------------------------------------------------------------------
// Les dimensions, mises en mots
// ---------------------------------------------------------------------------

/**
 * Une ligne réglée vue comme un support, pour `chargement.ts`.
 *
 * Les listes sans dimensions n'ont pas ces colonnes : `undefined` y devient
 * `null`, et le calcul répondra « dimensions absentes » — ce qui est vrai.
 */
function commeSupport(ligne: LigneReglee): Support {
  return {
    nom: ligne.nom,
    abrege: ligne.abrege ?? null,
    longueur_cm: ligne.longueur_cm ?? null,
    largeur_cm: ligne.largeur_cm ?? null,
    hauteur_cm: ligne.hauteur_cm ?? null,
    etages: ligne.etages ?? 1,
  };
}

function mlDe(ligne: LigneReglee): number | null {
  return metresLineaires(commeSupport(ligne));
}

/** « 120 × 80 × 110 », ou ce qui manque. */
function dimensionsDites(ligne: LigneReglee): string {
  const l = ligne.longueur_cm ?? null;
  const g = ligne.largeur_cm ?? null;
  const h = ligne.hauteur_cm ?? null;
  const etages = ligne.etages ?? 1;
  // Les étages tiennent dans cette colonne plutôt que dans la leur : ils
  // décrivent le même support, et une colonne de plus faisait déborder le
  // tableau de 114 px en 1280 — mesuré le 04/09/2026. Ils ne s'écrivent que
  // s'il y en a plus d'un : « ×1 » sur chaque ligne serait du bruit.
  const gerbage = etages > 1 ? ` · ${etages} étages` : '';
  if (l === null && g === null && h === null) {
    return etages > 1 ? `à renseigner${gerbage}` : 'à renseigner';
  }
  const dit = (v: number | null) => (v === null ? '?' : String(v));
  return `${dit(l)} × ${dit(g)} × ${dit(h)}${gerbage}`;
}

/**
 * Pourquoi cette ligne n'a pas de mètre linéaire, EN FRANÇAIS.
 *
 * Affiché en infobulle sur la cellule. Un tiret seul laisserait croire à une
 * panne ; ici on dit ce qui manque, et l'exploitation sait quoi faire.
 */
function pourquoiPasDeMl(ligne: LigneReglee): string | null {
  const r = poser(commeSupport(ligne));
  if (!refuse(r)) return null;
  if (r.refus === 'dimensions_absentes') {
    return 'Longueur et largeur manquantes : aucun mètre linéaire ne se calcule, et ce support n’apparaîtra dans aucune jauge de remplissage.';
  }
  if (r.refus === 'trop_large') {
    return `Ce support dépasse ${LARGEUR_DE_REFERENCE_CM} cm dans les deux sens : il ne se pose sur aucune largeur de référence, et aucun mètre linéaire ne se calcule.`;
  }
  return 'Le nombre d’étages doit valoir au moins 1.';
}

/** Ce que la boîte affiche pendant qu'on tape — le résultat, avant d'enregistrer. */
function apercuDuCalcul(renommage: Renommage): string {
  const nombre = (v: string) => {
    const propre = v.trim();
    return propre === '' || !/^[0-9]+$/.test(propre) ? null : Number(propre);
  };
  const etages = nombre(renommage.etages) ?? 1;
  const r = poser({
    nom: renommage.nom,
    abrege: renommage.abrege,
    longueur_cm: nombre(renommage.longueur),
    largeur_cm: nombre(renommage.largeur),
    hauteur_cm: nombre(renommage.hauteur),
    etages,
  });

  if (refuse(r)) {
    if (r.refus === 'dimensions_absentes') {
      return 'Sans longueur ni largeur, aucun remplissage ne se calculera pour ce support.';
    }
    if (r.refus === 'trop_large') {
      return `Plus large que ${LARGEUR_DE_REFERENCE_CM} cm dans les deux sens : rien ne se calculera.`;
    }
    return 'Le nombre d’étages doit valoir au moins 1.';
  }

  // 13,20 ML : la capacité d'une semi. Écrite ici pour donner un repère
  // parlant, et non pour dire quoi que ce soit d'une remorque en particulier.
  const dansUneSemi = combienTiennent(
    {
      nom: renommage.nom,
      abrege: renommage.abrege,
      longueur_cm: nombre(renommage.longueur),
      largeur_cm: nombre(renommage.largeur),
      hauteur_cm: nombre(renommage.hauteur),
      etages,
    },
    13.2,
  );

  return (
    `Sur ${LARGEUR_DE_REFERENCE_CM} cm de large : ${r.pose.deFront} de front, ` +
    `${r.pose.etages} étage${r.pose.etages > 1 ? 's' : ''}, ` +
    `${mlLisible(r.pose.ml)} ML l’unité` +
    (dansUneSemi === null ? '' : ` — soit ${dansUneSemi} dans une semi de 13,20 ML.`)
  );
}

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

/**
 * LES ONGLETS DE L'ÉCRAN. Les quatre listes réglées, plus l'IMPORT.
 *
 * SORTIS DE `VueParametres` le 07/09/2026, quand l'import a ouvert. La barre
 * doit surmonter DEUX corps différents — un tableau de liste ou l'écran
 * d'import —, et la laisser à l'intérieur de l'un des deux aurait obligé à la
 * recopier dans l'autre. Deux barres jumelles divergent au premier changement
 * porté d'un seul côté ; c'est exactement ce qui était arrivé à la barre haute
 * le 03/09.
 *
 * L'IMPORT EST UN ONGLET ET NON UN SOUS-MENU. Le module Paramètres n'a
 * volontairement aucun sous-menu (voir `Application.tsx`) : ses écrans se
 * lisent d'affilée, et une barre de plus au-dessus d'un tableau de quatre
 * lignes ferait deux niveaux pour rien.
 */
export type OngletParametres = CleListe | 'import';

export function OngletsParametres({
  onglet,
  quandOnglet,
}: {
  readonly onglet: OngletParametres;
  readonly quandOnglet: (choisi: OngletParametres) => void;
}) {
  return (
    <div className="onglets-listes">
      {LISTES.map((liste) => (
        <button
          key={liste.cle}
          type="button"
          className={liste.cle === onglet ? 'onglet-liste actif' : 'onglet-liste'}
          onClick={() => quandOnglet(liste.cle)}
        >
          {liste.onglet}
        </button>
      ))}
      <button
        type="button"
        className={onglet === 'import' ? 'onglet-liste actif' : 'onglet-liste'}
        onClick={() => quandOnglet('import')}
      >
        Import
      </button>
    </div>
  );
}

export interface ProprietesVueParametres {
  readonly cle: CleListe;
  readonly etat: Etat;
  readonly nouveau: { readonly nom: string; readonly abrege: string };
  readonly renommage: Renommage | null;
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly quandNouveau: (champ: 'nom' | 'abrege', valeur: string) => void;
  readonly quandAjouter: () => void;
  readonly quandRenommer: (ligne: LigneReglee) => void;
  readonly quandChampRenommage: (champ: ChampBoite, valeur: string) => void;
  readonly quandEnregistrerRenommage: () => void;
  readonly quandFermerRenommage: () => void;
  readonly quandBasculerActif: (ligne: LigneReglee) => void;
  readonly quandBasculerEchange: (ligne: LigneReglee) => void;
  readonly quandSupprimer: (ligne: LigneReglee) => void;
}

export function VueParametres({
  cle,
  etat,
  nouveau,
  renommage,
  refus,
  envoi,
  quandNouveau,
  quandAjouter,
  quandRenommer,
  quandChampRenommage,
  quandEnregistrerRenommage,
  quandFermerRenommage,
  quandBasculerActif,
  quandBasculerEchange,
  quandSupprimer,
}: ProprietesVueParametres) {
  const dit = descripteur(cle);
  const lignes = etat.nom === 'prete' ? etat.lignes : [];
  const eteintes = lignes.filter((l) => !l.actif).length;

  return (
    <div className="parametres">
      <form
        className="barre-liste-reglee"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          quandAjouter();
        }}
      >
        <span className="decompte">
          {etat.nom === 'chargement'
            ? 'Chargement…'
            : etat.nom === 'panne'
              ? 'Stafa ne répond pas.'
              : `${lignes.length} ${dit.titre}${lignes.length > 1 ? 's' : ''}${
                  eteintes > 0
                    ? `, dont ${eteintes} désactivé${eteintes > 1 ? 's' : ''}`
                    : ''
                }`}
        </span>
        <input
          className="champ"
          type="text"
          value={nouveau.nom}
          placeholder={dit.invite}
          disabled={envoi}
          onChange={(evenement) => quandNouveau('nom', evenement.target.value)}
        />
        {dit.abrege && (
          <input
            className="champ court"
            type="text"
            value={nouveau.abrege}
            placeholder="Abrégé"
            disabled={envoi}
            onChange={(evenement) => quandNouveau('abrege', evenement.target.value)}
          />
        )}
        <button className="bouton" type="submit" disabled={envoi}>
          Ajouter
        </button>
      </form>

      <p className="aide-liste">{dit.aide}</p>
      {refus === null ? null : <p className="bandeau-refus">{refus}</p>}

      <div className="tableau-cadre">
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              {dit.abrege && <th>Abrégé</th>}
              {dit.dimensions && <th className="col-large">Dimensions (cm)</th>}
              {dit.dimensions && <th>ML / unité</th>}
              {dit.echange && <th>Échangeable</th>}
              <th>Emploi</th>
              <th>État</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {lignes.length === 0 && etat.nom === 'prete' && (
              <tr>
                <td
                  colSpan={
                    (dit.abrege ? 5 : 4) +
                    (dit.dimensions ? 2 : 0) +
                    (dit.echange ? 1 : 0)
                  }
                  className="rien"
                >
                  Aucun {dit.titre} pour l’instant. Ajoutez le premier ci-dessus.
                </td>
              </tr>
            )}
            {lignes.map((ligne) => (
              <tr key={ligne.id} className={ligne.actif ? undefined : 'eteinte'}>
                <td>{ligne.nom}</td>
                {dit.abrege && (
                  <td className="code">
                    {ligne.abrege === null || ligne.abrege === undefined
                      ? '—'
                      : ligne.abrege}
                  </td>
                )}
                {dit.dimensions && (
                  <td className="code col-large">{dimensionsDites(ligne)}</td>
                )}
                {dit.dimensions && (
                  <td className="code" title={pourquoiPasDeMl(ligne) ?? undefined}>
                    {mlLisible(mlDe(ligne))}
                  </td>
                )}
                {/* UNE CASE, PAS UN BOUTON « Rendre échangeable » : c'est un
                    état à lire d'un coup d'œil sur toute la colonne, et
                    l'exploitation en coche plusieurs d'affilée. Elle écrit
                    immédiatement — il n'y a rien à valider. */}
                {dit.echange && (
                  <td>
                    <label className="case-echangeable">
                      <input
                        type="checkbox"
                        checked={ligne.echangeable === true}
                        disabled={envoi}
                        onChange={() => quandBasculerEchange(ligne)}
                      />
                      <span className="sourdine">
                        {ligne.echangeable === true ? 'Consigné' : 'Perdu'}
                      </span>
                    </label>
                  </td>
                )}
                <td className="sourdine">
                  {ligne.emploi === 0 ? 'jamais employé' : dit.compte(ligne.emploi)}
                </td>
                <td>
                  <span className={ligne.actif ? 'etat-liste' : 'etat-liste eteint'}>
                    {ligne.actif ? 'Actif' : 'Désactivé'}
                  </span>
                </td>
                <td className="actions-liste">
                  <button
                    className="lien-action"
                    type="button"
                    disabled={envoi}
                    onClick={() => quandRenommer(ligne)}
                  >
                    {/* « Modifier » dès qu'il y a autre chose qu'un nom à
                        changer : le bouton doit dire ce que la boîte fait, et
                        elle porte quatre champs de plus sur les supports.
                        Constaté en REGARDANT la page rendue le 04/09/2026. */}
                    {dit.dimensions ? 'Modifier' : 'Renommer'}
                  </button>
                  <button
                    className="lien-action"
                    type="button"
                    disabled={envoi}
                    onClick={() => quandBasculerActif(ligne)}
                  >
                    {ligne.actif ? 'Désactiver' : 'Réactiver'}
                  </button>
                  {ligne.emploi === 0 && (
                    <button
                      className="lien-action risque"
                      type="button"
                      disabled={envoi}
                      onClick={() => quandSupprimer(ligne)}
                    >
                      Supprimer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {renommage === null ? null : (
        <div className="voile-boite" role="presentation">
          <div className="boite-decrochage" role="dialog" aria-modal="true">
            <h3>
              {dit.dimensions ? 'Modifier' : 'Renommer'} un {dit.titre}
            </h3>
            <p className="quoi-decroche">
              {renommage.emploi === 0
                ? 'Jamais employé pour l’instant.'
                : cle === 'types'
                  ? `Porté par ${dit.compte(renommage.emploi)}, qui suivront le nouveau nom.`
                  : `Employé par ${dit.compte(renommage.emploi)} déjà enregistrées, qui ne changeront pas.`}
            </p>
            <form
              onSubmit={(evenement) => {
                evenement.preventDefault();
                quandEnregistrerRenommage();
              }}
            >
              <div className={dit.abrege ? 'deux-boites' : undefined}>
                <div>
                  <label className="etiquette-boite" htmlFor="renommage-nom">
                    Nom
                  </label>
                  <input
                    id="renommage-nom"
                    className="champ-boite"
                    type="text"
                    value={renommage.nom}
                    disabled={envoi}
                    onChange={(evenement) =>
                      quandChampRenommage('nom', evenement.target.value)
                    }
                  />
                </div>
                {dit.abrege && (
                  <div>
                    <label className="etiquette-boite" htmlFor="renommage-abrege">
                      Abrégé
                    </label>
                    <input
                      id="renommage-abrege"
                      className="champ-boite"
                      type="text"
                      value={renommage.abrege}
                      disabled={envoi}
                      onChange={(evenement) =>
                        quandChampRenommage('abrege', evenement.target.value)
                      }
                    />
                  </div>
                )}
              </div>
              {dit.dimensions && (
                <>
                  <div className="trois-boites">
                    {(
                      [
                        ['longueur', 'Longueur'],
                        ['largeur', 'Largeur'],
                        ['hauteur', 'Hauteur'],
                      ] as const
                    ).map(([champ, etiquette]) => (
                      <div key={champ}>
                        <label
                          className="etiquette-boite"
                          htmlFor={`renommage-${champ}`}
                        >
                          {etiquette}
                        </label>
                        <input
                          id={`renommage-${champ}`}
                          className="champ-boite"
                          type="text"
                          inputMode="numeric"
                          value={renommage[champ]}
                          disabled={envoi}
                          onChange={(evenement) =>
                            quandChampRenommage(champ, evenement.target.value)
                          }
                        />
                        <p className="unite-boite">cm</p>
                      </div>
                    ))}
                  </div>
                  <div className="deux-boites">
                    <div>
                      <label className="etiquette-boite" htmlFor="renommage-etages">
                        Étages empilables
                      </label>
                      <input
                        id="renommage-etages"
                        className="champ-boite"
                        type="text"
                        inputMode="numeric"
                        value={renommage.etages}
                        disabled={envoi}
                        onChange={(evenement) =>
                          quandChampRenommage('etages', evenement.target.value)
                        }
                      />
                      <p className="unite-boite">
                        1 = on ne gerbe pas. C’est vous qui savez, pas le logiciel.
                      </p>
                    </div>
                    <div />
                  </div>
                  <p className="apercu-boite">{apercuDuCalcul(renommage)}</p>
                </>
              )}
              {refus !== null && <p className="faute-boite">{refus}</p>}
              <div className="pied-boite">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  disabled={envoi}
                  onClick={quandFermerRenommage}
                >
                  Annuler
                </button>
                <button className="bouton" type="submit" disabled={envoi}>
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesParametres {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

export function EcranParametres({ jeton, quandAccesPerdu }: ProprietesParametres) {
  const [cle, setCle] = useState<CleListe>('supports');
  /**
   * L'onglet IMPORT est un drapeau A COTE de la liste courante, et non une
   * cinquieme valeur de `cle`.
   *
   * La raison est concrete : `cle` commande le chargement des lignes reglees.
   * Y ajouter « import » obligerait chaque lecture a s'en garder, et revenir
   * de l'import rechargerait une liste qu'on n'a pas quittee. Ainsi, aller a
   * l'import et revenir retrouve l'onglet ou l'on etait, sans un appel.
   */
  const [importOuvert, setImportOuvert] = useState(false);
  const [etat, setEtat] = useState<Etat>({ nom: 'chargement' });
  const [nouveau, setNouveau] = useState({ nom: '', abrege: '' });
  const [renommage, setRenommage] = useState<Renommage | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * Les motifs de litige ne passent pas par les mêmes routes que les deux
   * autres listes : ils existaient avant cet écran, sous `/motifs-litige`. On
   * les ramène ici à la MÊME FORME plutôt que de dessiner un troisième tableau.
   */
  const charger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    setEtat({ nom: 'chargement' });

    const lu: Resultat<readonly LigneReglee[]> =
      cle === 'motifs'
        ? await (async () => {
            const motifs = await lireMotifsLitige(j);
            return motifs.etat === 'ok'
              ? ({
                  etat: 'ok',
                  valeur: motifs.valeur.map((m) => ({
                    id: m.id,
                    nom: m.nom,
                    actif: m.actif,
                    emploi: m.emploi ?? 0,
                  })),
                } as const)
              : motifs;
          })()
        : await lireListeReglee(j, listeDe(cle));

    if (lu.etat !== 'ok') {
      if (lu.cause === 'acces') quandAccesPerdu();
      setEtat({ nom: 'panne' });
      return;
    }
    setEtat({ nom: 'prete', lignes: lu.valeur });
  }, [avecJeton, cle, quandAccesPerdu]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Une écriture, puis un rechargement : la base a le dernier mot. */
  const ecrire = (
    action: (j: string) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>,
    apres?: () => void,
  ) => {
    setEnvoi(true);
    setRefus(null);
    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setEnvoi(false);
        return;
      }
      const resultat = await action(j);
      if (resultat.etat === 'ok') {
        await charger();
        setEnvoi(false);
        if (apres !== undefined) apres();
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setRefus(REFUS[resultat.cause]);
      setEnvoi(false);
    })();
  };

  const listeApi = listeDe(cle);

  return (
    <main className="corps corps-parametres">
      <OngletsParametres
        onglet={importOuvert ? 'import' : cle}
        quandOnglet={(choisi) => {
          if (choisi === 'import') {
            setImportOuvert(true);
            return;
          }
          setImportOuvert(false);
          setCle(choisi);
          setNouveau({ nom: '', abrege: '' });
          setRenommage(null);
          setRefus(null);
        }}
      />
      {importOuvert ? (
        <EcranImport jeton={jeton} quandAccesPerdu={quandAccesPerdu} />
      ) : (
        <VueParametres
          cle={cle}
          etat={etat}
          nouveau={nouveau}
          renommage={renommage}
          refus={refus}
          envoi={envoi}
          quandNouveau={(champ, valeur) =>
            setNouveau((actuel) => ({ ...actuel, [champ]: valeur }))
          }
          quandAjouter={() => {
            if (nouveau.nom.trim() === '') {
              setRefus('Le nom est obligatoire.');
              return;
            }
            const nom = nouveau.nom.trim();
            const abrege = nouveau.abrege.trim();
            ecrire(
              (j) =>
                cle === 'motifs'
                  ? creerMotifLitige(j, nom)
                  : creerLigneReglee(j, listeApi, {
                      nom,
                      ...(cle === 'supports' && abrege !== '' ? { abrege } : {}),
                    }),
              () => setNouveau({ nom: '', abrege: '' }),
            );
          }}
          quandRenommer={(ligne) =>
            setRenommage({
              id: ligne.id,
              nom: ligne.nom,
              abrege: ligne.abrege ?? '',
              // En CHAÎNE, et vide quand la valeur est absente : le champ doit
              // pouvoir rester vide, et un « 0 » affiché d'office serait une
              // dimension inventée.
              longueur:
                ligne.longueur_cm === null || ligne.longueur_cm === undefined
                  ? ''
                  : String(ligne.longueur_cm),
              largeur:
                ligne.largeur_cm === null || ligne.largeur_cm === undefined
                  ? ''
                  : String(ligne.largeur_cm),
              hauteur:
                ligne.hauteur_cm === null || ligne.hauteur_cm === undefined
                  ? ''
                  : String(ligne.hauteur_cm),
              etages: String(ligne.etages ?? 1),
              emploi: ligne.emploi,
            })
          }
          quandChampRenommage={(champ, valeur) =>
            setRenommage((actuel) =>
              actuel === null ? null : { ...actuel, [champ]: valeur },
            )
          }
          quandEnregistrerRenommage={() => {
            if (renommage === null) return;
            const nom = renommage.nom.trim();
            if (nom === '') {
              setRefus('Le nom est obligatoire.');
              return;
            }
            const abrege = renommage.abrege.trim();

            // Une saisie qui n'est pas un nombre n'est PAS envoyée : `undefined`
            // laisse la valeur inchangée, là où `null` l'effacerait. Taper « abc »
            // dans la longueur ne doit pas supprimer la longueur enregistrée.
            const dimensions = descripteur(cle).dimensions
              ? {
                  ...retenirSiConnu('longueur_cm', nombreSaisi(renommage.longueur)),
                  ...retenirSiConnu('largeur_cm', nombreSaisi(renommage.largeur)),
                  ...retenirSiConnu('hauteur_cm', nombreSaisi(renommage.hauteur)),
                  // `etages` est `not null` en base : un champ vide le ramène à 1
                  // plutôt que de faire échouer l'écriture.
                  etages: nombreSaisi(renommage.etages) ?? 1,
                }
              : {};

            ecrire(
              (j) =>
                cle === 'motifs'
                  ? changerMotifLitige(j, renommage.id, { nom })
                  : modifierLigneReglee(j, listeApi, renommage.id, {
                      nom,
                      ...(cle === 'supports'
                        ? { abrege: abrege === '' ? null : abrege }
                        : {}),
                      ...dimensions,
                    }),
              () => setRenommage(null),
            );
          }}
          quandFermerRenommage={() => {
            setRenommage(null);
            setRefus(null);
          }}
          quandBasculerActif={(ligne) =>
            ecrire((j) =>
              cle === 'motifs'
                ? changerMotifLitige(j, ligne.id, { actif: !ligne.actif })
                : modifierLigneReglee(j, listeApi, ligne.id, { actif: !ligne.actif }),
            )
          }
          /* Seuls les supports la portent : l'API ne lit `echangeable` que si la
           liste le déclare (`apps/api/src/parametres.ts` ligne 252). La case
           n'est de toute façon dessinée que là. */
          quandBasculerEchange={(ligne) =>
            ecrire((j) =>
              modifierLigneReglee(j, listeApi, ligne.id, {
                echangeable: !(ligne.echangeable === true),
              }),
            )
          }
          quandSupprimer={(ligne) =>
            ecrire((j) =>
              cle === 'motifs'
                ? supprimerMotifLitige(j, ligne.id)
                : supprimerLigneReglee(j, listeApi, ligne.id),
            )
          }
        />
      )}
    </main>
  );
}
